"use client";

import {
  forwardRef,
  type ButtonHTMLAttributes,
  type AnchorHTMLAttributes,
  type ReactNode,
} from "react";
import Link, { type LinkProps } from "next/link";
import { Loader2 } from "lucide-react";

export type AeraButtonVariant =
  | "primary"
  | "secondary"
  | "outline"
  | "ghost"
  | "ghost-dt"
  | "nav-inactive"
  | "danger"
  | "line"
  | "facebook"
  | "warm"
  | "link"
  | "transparent"
  | "signin"
  | "lightgray"
  | "disabled";

export type AeraButtonSize = "xxs" | "xs" | "sm" | "md" | "lg";
export type AeraButtonTextSize = "xxs" | "xs" | "sm" | "md" | "bs" | "lg";
export type AeraButtonPaddingSize =
  | "none"
  | "xxs"
  | "xs"
  | "sm"
  | "md"
  | "lg"
  | "xl";
export type AeraButtonTextColor =
  | "default"
  | "white"
  | "black"
  | "charcoal"
  | "gray"
  | "amber"
  | "amberdark"
  | "darkamber"
  | "red"
  | "green"
  | "muted"
  | "subtle";
export type AeraButtonFontWeight =
  | "normal"
  | "medium"
  | "semibold"
  | "bold"
  | "extrabold";

type SharedProps = {
  variant?: AeraButtonVariant;
  size?: AeraButtonSize;
  textSize?: AeraButtonTextSize;
  paddingSize?: AeraButtonPaddingSize;
  textColor?: AeraButtonTextColor;
  fontWeight?: AeraButtonFontWeight;
  loading?: boolean;
  fullWidth?: boolean;
  icon?: ReactNode;
  iconRight?: ReactNode;
};

type ButtonAsButton = SharedProps &
  ButtonHTMLAttributes<HTMLButtonElement> & {
    href?: undefined;
    as?: "button";
  };

type ButtonAsLink = SharedProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof LinkProps> &
  LinkProps & {
    as?: "link";
    disabled?: boolean;
  };

type ButtonAsAnchor = SharedProps &
  AnchorHTMLAttributes<HTMLAnchorElement> & {
    as: "a";
    disabled?: boolean;
  };

export type AeraButtonProps = ButtonAsButton | ButtonAsLink | ButtonAsAnchor;

const variantBgClasses: Record<AeraButtonVariant, string> = {
  "ghost-dt": "hover:bg-aera-lightgray/50",
  "nav-inactive": "bg-aera-offwhite hover:bg-aera-warmcream/50",

  primary:
    "bg-aera-amber hover:bg-aera-charcoal border border-aera-amber hover:border-aera-charcoal text-aera-charcoal hover:text-aera-white rounded-xl",
  secondary:
    "bg-aera-charcoal hover:bg-aera-amber border border-aera-charcoal hover:border-aera-amber text-aera-white hover:text-aera-charcoal rounded-xl",
  warm: "bg-aera-warmcream hover:bg-aera-amberlight/50 border border-aera-amber/30 hover:border-aera-amber text-aera-charcoal rounded-xl",
  outline:
    "bg-aera-white hover:bg-aera-offwhite border border-aera-lightgray/80 hover:border-aera-amber text-aera-charcoal rounded-xl",
  transparent: "hover:bg-aera-lightgray/60 rounded-lg",
  ghost: "rounded-sm",
  link: "bg-transparent p-0",
  signin:
    "group relative overflow-hidden bg-aera-white border border-aera-lightgray/80 hover:border-aera-amber/40 rounded-2xl",
  lightgray: "bg-aera-lightgray rounded-xl",
  disabled: "border rounded-lg",
  danger:
    "bg-aera-redlight text-aera-red hover:text-aera-white border border-aera-red hover:bg-aera-red rounded-xl",
  line: "bg-[#06C755] hover:bg-[#05B84D] border border-[#06C755] hover:border-[#05B84D] text-aera-white rounded-lg",
  facebook: "bg-blue-600 hover:bg-blue-700 text-aera-white rounded-lg",
};

const textColorClasses: Record<AeraButtonTextColor, string> = {
  default: "",
  white: "text-aera-white",
  black: "text-aera-black",
  charcoal: "text-aera-charcoal",
  gray: "text-aera-gray",
  amber: "text-aera-amber",
  amberdark: "text-aera-amberdark",
  darkamber: "text-aera-darkamber",
  red: "text-aera-red",
  green: "text-aera-green",
  muted: "text-aera-gray",
  subtle: "text-aera-lightslate",
};

const fontWeightClasses: Record<AeraButtonFontWeight, string> = {
  normal: "font-normal",
  medium: "font-medium",
  semibold: "font-semibold",
  bold: "font-bold",
  extrabold: "font-extrabold",
};

const paddingClasses: Record<AeraButtonPaddingSize, string> = {
  none: "p-0",
  xxs: "px-1.5 py-1",
  xs: "px-2 sm:px-3 py-1.75 sm:py-1.75",
  sm: "px-3 py-1.5",
  md: "px-3 py-2.5",
  lg: "px-4 py-3.5",
  xl: "px-8 py-3",
};

const textClasses: Record<AeraButtonTextSize, string> = {
  xxs: "text-xxs",
  xs: "text-xs",
  sm: "text-sm",
  md: "text-sm",
  bs: "text-base",
  lg: "text-lg",
};

const AeraButton = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  AeraButtonProps
>((props, ref) => {
  const {
    variant = "primary",
    size = "md",
    textSize,
    paddingSize,
    textColor,
    fontWeight,
    loading = false,
    fullWidth = false,
    icon,
    iconRight,
    disabled,
    className = "",
    children,
    ...rest
  } = props;

  const isDisabled = disabled || loading;

  const isLink = variant === "link";

  const classes = [
    "group inline-flex items-center justify-center gap-2 cursor-pointer select-none transition-colors duration-200 ease-out focus:outline-none focus-visible:outline-none",
    isDisabled ? "opacity-50 cursor-not-allowed pointer-events-none" : "",
    variantBgClasses[variant],
    textColor ? textColorClasses[textColor] : "",
    fontWeight ? fontWeightClasses[fontWeight] : "",
    isLink ? "" : paddingClasses[paddingSize ?? size],
    isLink ? "" : textClasses[textSize ?? size],
    fullWidth ? "w-full" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const content = (
    <>
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      ) : icon ? (
        <span className="shrink-0" aria-hidden>
          {icon}
        </span>
      ) : null}
      {children}
      {iconRight && !loading ? (
        <span
          className="shrink-0 relative inline-flex items-center justify-center"
          aria-hidden
        >
          {iconRight}
        </span>
      ) : null}
    </>
  );

  // Render as <a> tag
  if ("as" in props && props.as === "a") {
    const {
      as: _as,
      variant: _v,
      size: _s,
      textSize: _ts,
      paddingSize: _ps,
      textColor: _tc,
      fontWeight: _fw,
      loading: _l,
      fullWidth: _f,
      icon: _i,
      iconRight: _ir,
      disabled: _d,
      className: _cn,
      children: _children,
      ...restAnchorProps
    } = props as ButtonAsAnchor;
    return (
      <a
        ref={ref as React.Ref<HTMLAnchorElement>}
        {...restAnchorProps}
        className={classes}
        aria-disabled={isDisabled || undefined}
      >
        {content}
      </a>
    );
  }

  // Render as Next.js <Link>
  if ("href" in props && props.href !== undefined) {
    const {
      as: _,
      variant: _v,
      size: _s,
      textSize: _ts,
      paddingSize: _ps,
      textColor: _tc,
      fontWeight: _fw,
      loading: _l,
      fullWidth: _f,
      icon: _i,
      iconRight: _ir,
      disabled: _d,
      className: _cn,
      ...linkProps
    } = props as ButtonAsLink;
    return (
      <Link
        ref={ref as React.Ref<HTMLAnchorElement>}
        {...linkProps}
        className={classes}
        aria-disabled={isDisabled || undefined}
      >
        {content}
      </Link>
    );
  }

  // Default: render as <button>
  const {
    as: _,
    variant: _v,
    size: _s,
    textSize: _ts,
    paddingSize: _ps,
    textColor: _tc,
    fontWeight: _fw,
    loading: _l,
    fullWidth: _f,
    icon: _i,
    iconRight: _ir,
    className: _cn,
    ...buttonProps
  } = rest as any;
  return (
    <button
      ref={ref as React.Ref<HTMLButtonElement>}
      disabled={isDisabled}
      {...buttonProps}
      className={classes}
    >
      {content}
    </button>
  );
});

AeraButton.displayName = "AeraButton";

export { AeraButton };
